// slashPlace — where the "/" menu opens (pen Q73, "/ in the middle of a
// line"): its left edge under the "/", 6 px below the line; moved left so it
// stays inside the page; above the line when there is no room below.
//
// `placeMenu` is pure (rectangles in, a place out); `slashPointIn` is the one
// DOM read: where the "/" (or, once picked, the end of the text) stands in a
// block, in viewport pixels. A view measurement only: nothing domain-shaped
// is derived or kept.

export type { MenuPlace };
export { MENU_GAP, placeMenu, slashPointIn };

// The gap between the line and the menu (pen Q73: 6 px), a layout constant
// of the pointer geometry like Popover's SIDE_OFFSET.
const MENU_GAP = 6;

type Point = {
    readonly x: number;
    // The line's top and bottom at that point.
    readonly top: number;
    readonly bottom: number;
};

type Rect = {
    readonly left: number;
    readonly top: number;
    readonly right: number;
    readonly bottom: number;
};

// Offsets from the menu's positioned parent.
type MenuPlace = {
    readonly left: number;
    readonly top: number;
    readonly side: "below" | "above";
};

// `point` and `page` in viewport pixels; `parent` is the menu's positioned
// parent; `menu` its size; `viewportHeight` the window's.
const placeMenu = ({
    point,
    parent,
    page,
    menu,
    viewportHeight,
}: {
    readonly point: Point;
    readonly parent: Rect;
    readonly page: Rect;
    readonly menu: { readonly width: number; readonly height: number };
    readonly viewportHeight: number;
}): MenuPlace => {
    const maxLeft = Math.max(page.left, page.right - menu.width);
    const x = Math.min(Math.max(point.x, page.left), maxLeft);
    const below = point.bottom + MENU_GAP + menu.height <= viewportHeight;
    const above = point.top - MENU_GAP - menu.height >= 0;
    const side = below || !above ? "below" : "above";
    return {
        left: x - parent.left,
        top:
            side === "below"
                ? point.bottom + MENU_GAP - parent.top
                : point.top - MENU_GAP - menu.height - parent.top,
        side,
    };
};

// The field's properties that decide where its text wraps.
const MIRRORED: readonly string[] = [
    "box-sizing",
    "width",
    "padding-top",
    "padding-right",
    "padding-bottom",
    "padding-left",
    "border-top-width",
    "border-right-width",
    "border-bottom-width",
    "border-left-width",
    "font-family",
    "font-size",
    "font-weight",
    "font-style",
    "letter-spacing",
    "line-height",
    "text-indent",
    "word-spacing",
    "tab-size",
];

// A textarea's caret point at `index`, by a mirror that copies its type and
// wraps the text before the index the same way (the field draws no DOM for
// its text). Cost: O(text length).
const textareaPoint = (
    field: HTMLTextAreaElement,
    index: number,
): Point | undefined => {
    const doc = field.ownerDocument;
    const view = doc.defaultView;
    if (view === null) return undefined;
    const style = view.getComputedStyle(field);
    const mirror = doc.createElement("div");
    for (const prop of MIRRORED)
        mirror.style.setProperty(prop, style.getPropertyValue(prop));
    mirror.style.position = "absolute";
    mirror.style.visibility = "hidden";
    mirror.style.whiteSpace = "pre-wrap";
    mirror.style.overflowWrap = "break-word";
    const box = field.getBoundingClientRect();
    mirror.style.left = `${box.left + view.scrollX}px`;
    mirror.style.top = `${box.top + view.scrollY}px`;
    mirror.textContent = field.value.slice(0, index);
    const mark = doc.createElement("span");
    mark.textContent = field.value.slice(index) || ".";
    mirror.appendChild(mark);
    doc.body.appendChild(mirror);
    const r = mark.getBoundingClientRect();
    const line = Number.parseFloat(style.lineHeight) || r.height;
    mirror.remove();
    return { x: r.left, top: r.top, bottom: r.top + line };
};

// The end of a block's reading face: a range after its last character; an
// empty line (its placeholder shows) is its start, on its first line.
const textEndPoint = (block: HTMLElement): Point | undefined => {
    const doc = block.ownerDocument;
    const face = block.querySelector<HTMLElement>(
        ".bilda-editable-text--display",
    );
    if (face === null) return undefined;
    if (face.classList.contains("bilda-editable-text--placeholder")) {
        const box = face.getBoundingClientRect();
        const line =
            Number.parseFloat(
                doc.defaultView?.getComputedStyle(face).lineHeight ?? "",
            ) || box.height;
        return { x: box.left, top: box.top, bottom: box.top + line };
    }
    const walker = doc.createTreeWalker(face, NodeFilter.SHOW_TEXT);
    let last: Text | undefined;
    for (let n = walker.nextNode(); n !== null; n = walker.nextNode())
        if (n instanceof Text && n.data.trim() !== "") last = n;
    if (last === undefined) return undefined;
    const range = doc.createRange();
    range.setStart(last, last.data.length);
    range.setEnd(last, last.data.length);
    const r = range.getBoundingClientRect();
    return r.width === 0 && r.height === 0 && r.left === 0
        ? undefined
        : { x: r.left, top: r.top, bottom: r.bottom };
};

// Where the "/" stands in `block`: in its field while it is being typed (the
// last "/" before the caret), else at the end of its text (the "/" has
// been picked and dropped). Absent: nothing to measure (the menu keeps its
// place under the block).
const slashPointIn = (block: HTMLElement): Point | undefined => {
    const field = block.querySelector("textarea");
    if (field !== null) {
        const caret = field.selectionStart ?? field.value.length;
        const slash = field.value.lastIndexOf("/", Math.max(0, caret - 1));
        return textareaPoint(field, slash < 0 ? caret : slash);
    }
    return textEndPoint(block);
};
